import {
  DEFAULT_BRANDING,
  isBrandingEnabled,
  resolveBranding,
  emitBrandingCssVars,
  emitBrandingHeader,
  emitBrandingDescription,
  PORTAL_BRAND_CSS
} from './branding';
import {
  getEnabledPortalFields,
  hasEnabledPortalFields,
  filterRowCardsByPortalFields
} from './portal_fields';

/**
 * @param {string} text
 */
function escHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * @param {import('./config_schema.js').PortalConfig} config
 * @returns {string}
 */
export function generatePageHtml(config) {
  const sections = config.html?.sections || {};

  if (sections.minimal?.enabled) {
    return emitMinimalHtml(config);
  }

  if (config.features?.shipmentTracker || sections.shippingTracker?.enabled) {
    return emitShippingTrackerHtml(config);
  }

  const urlParam = (config.sheet.urlParam || 'id').replace(/[^\w]/g, '') || 'id';
  const pageTitle = config.script?.pageTitle || 'Portal';
  const renewalDays = config.html?.sections?.renewForm?.renewalDays ?? config.script?.renewalDays ?? 14;
  const renewStatuses = config.html?.sections?.renewForm?.statusValues || ['Checked Out'];
  const brandingEnabled = isBrandingEnabled(config);
  const branding = resolveBranding(config);

  const lines = [];
  lines.push('<!DOCTYPE html>');
  lines.push('<html>');
  lines.push('  <head>');
  lines.push('    <base target="_top">');
  lines.push(`    <title>${escHtml(pageTitle)}</title>`);
  lines.push('    <style>');
  if (brandingEnabled) {
    lines.push(emitBrandingCssVars(branding));
    lines.push(PORTAL_BRAND_CSS);
  }
  lines.push(EMBED_CSS);
  lines.push('    </style>');
  lines.push('  </head>');
  lines.push('  <body>');
  lines.push('    <div class="wrap">');
  if (brandingEnabled) {
    emitBrandingHeader(lines, branding, { heading: pageTitle, escHtml });
  }

  if (sections.intro?.enabled) {
    lines.push('      <div class="intro">');
    if (sections.intro.title) {
      lines.push(`        <h1 class="intro-title">${escHtml(sections.intro.title)}</h1>`);
    }
    if (sections.intro.subtitle) {
      lines.push(`        <p class="intro-sub">${escHtml(sections.intro.subtitle)}</p>`);
    }
    if (sections.intro.body) {
      lines.push(`        <p class="muted">${escHtml(sections.intro.body)}</p>`);
    }
    lines.push('      </div>');
  }

  if (sections.search?.enabled) {
    lines.push('      <div class="card">');
    lines.push(`        <form method="get" action="<?= data.webappUrl ?>">`);
    lines.push('          <div class="row">');
    lines.push(`            <div class="label">${escHtml(sections.search.label || 'Search')}</div>`);
    lines.push(
      `            <input type="text" name="${urlParam}" placeholder="${escHtml(sections.search.placeholder || '')}" required class="input">`
    );
    lines.push('          </div>');
    lines.push('          <button type="submit" class="btn">');
    lines.push(`            ${escHtml(sections.search.buttonText || 'Search')}`);
    lines.push('          </button>');
    lines.push('        </form>');
    lines.push('      </div>');
  }

  if (config.features?.multiRowLookup && sections.loansList?.enabled) {
    lines.push(...emitLibraryLoans(config, urlParam, renewalDays, renewStatuses));
  } else if (sections.rowCards?.length) {
    const enabledFields = getEnabledPortalFields(config);
    const cards = filterRowCardsByPortalFields(sections.rowCards, enabledFields);
    if (cards.length) {
      lines.push(
        ...emitRowCards(config, { ...sections, rowCards: cards }, urlParam, brandingEnabled ? branding : null)
      );
    }
  } else if (hasEnabledPortalFields(config)) {
    lines.push(...emitDefaultPortalCard(config, sections, brandingEnabled ? branding : null));
  }

  lines.push('    </div>');
  lines.push('  </body>');
  lines.push('</html>');

  return lines.join('\n');
}

/**
 * @param {import('./config_schema.js').PortalConfig} config
 * @param {object} sections
 * @param {string} urlParam
 * @param {ReturnType<typeof resolveBranding>} branding
 */
/**
 * @param {import('./config_schema.js').PortalConfig} config
 * @param {object} sections
 * @param {ReturnType<typeof resolveBranding> | null} branding
 */
function emitDefaultPortalCard(config, sections, branding) {
  const enabled = getEnabledPortalFields(config);
  if (!enabled.length) return [];

  const lines = [];
  const notFound = sections.notFound?.message || 'Not found.';
  const bg = branding?.cardColor || DEFAULT_BRANDING.cardColor;
  const cardTitle = config.script?.pageTitle || 'Details';

  lines.push(`      <div class="card" style="background:${escHtml(bg)}">`);
  lines.push('        <? if (data.found) { ?>');
  lines.push(`          <h2 class="card-title">${escHtml(cardTitle)}</h2>`);
  for (const field of enabled) {
    lines.push('          <div class="row">');
    lines.push(`            <div class="label">${escHtml(field.label)}</div>`);
    lines.push(`            <div class="value"><?= data.${field.columnKey} ?></div>`);
    lines.push('          </div>');
  }
  lines.push('        <? } else { ?>');
  lines.push(`          <div class="status not-found">${escHtml(notFound)}</div>`);
  lines.push('        <? } ?>');
  lines.push('      </div>');

  return lines;
}

function emitRowCards(config, sections, urlParam, branding) {
  const lines = [];
  const notFound = sections.notFound?.message || 'Not found.';
  const defaultCardColor = DEFAULT_BRANDING.cardColor;

  for (const card of sections.rowCards || []) {
    const fullClass = card.fields?.some((f) => f.fullWidth) ? ' card full-width' : ' card';
    const bg = card.bg || branding?.cardColor || defaultCardColor;
    lines.push(`      <div class="${fullClass.trim()}" style="background:${escHtml(bg)}">`);
    lines.push('        <? if (data.found) { ?>');
    if (card.title) {
      lines.push(`          <h2 class="card-title">${escHtml(card.title)}</h2>`);
    }
    for (const f of card.fields || []) {
      lines.push('          <div class="row">');
      lines.push(`            <div class="label">${escHtml(f.label)}</div>`);
      lines.push(`            <div class="value"><?= data.${f.columnKey} ?></div>`);
      lines.push('          </div>');
    }
    lines.push('        <? } else { ?>');
    lines.push(`          <div class="status not-found">${escHtml(notFound)}</div>`);
    lines.push('        <? } ?>');
    lines.push('      </div>');
  }

  return lines;
}

function emitLibraryLoans(config, urlParam, renewalDays, renewStatuses) {
  const lines = [];
  const notFound = config.html?.sections?.notFound?.message || 'No loans found for this patron.';
  const showRenew = config.features?.doPostRenew && config.html?.sections?.renewForm?.enabled;
  const statusChecks = renewStatuses
    .map((s) => `String(loan.status).trim() === "${String(s).replace(/"/g, '\\"')}"`)
    .join(' || ');

  lines.push('      <? if (data.found) { ?>');
  lines.push('        <? for (var li = 0; li < data.loans.length; li++) { ?>');
  lines.push('          <? var loan = data.loans[li]; ?>');
  lines.push('          <div class="card">');
  lines.push('            <h2 class="card-title"><?= loan.bookTitle ?></h2>');
  lines.push('            <div class="row"><div class="label">Author</div><div class="value"><?= loan.author ?></div></div>');
  lines.push('            <div class="row"><div class="label">Status</div><div class="value"><?= loan.status ?></div></div>');
  lines.push('            <div class="row"><div class="label">Due date</div><div class="value"><?= loan.dueDate ?></div></div>');
  lines.push(
    '            <div class="row"><div class="label">Checked out</div><div class="value"><?= loan.checkedOutDate ?></div></div>'
  );
  lines.push(
    '            <div class="row"><div class="label">Shelf</div><div class="value"><?= loan.shelfLocation ?></div></div>'
  );

  if (showRenew) {
    lines.push('            <? if (' + (statusChecks || 'true') + ') { ?>');
    lines.push('              <form method="post" action="<?= data.postUrl ?>" class="renew-form">');
    lines.push('                <input type="hidden" name="action" value="renew">');
    lines.push('                <input type="hidden" name="checkoutId" value="<?= loan.checkoutId ?>">');
    lines.push(`                <input type="hidden" name="${urlParam}" value="<?= data.patronId ?>">`);
    lines.push(`                <button type="submit" class="btn">Renew ${renewalDays} days</button>`);
    lines.push('              </form>');
    lines.push('            <? } ?>');
  }

  lines.push('          </div>');
  lines.push('        <? } ?>');
  lines.push('      <? } else { ?>');
  lines.push('        <div class="card">');
  lines.push(`          <div class="status not-found">${escHtml(notFound)}</div>`);
  lines.push('        </div>');
  lines.push('      <? } ?>');

  return lines;
}

/**
 * @param {import('./config_schema.js').PortalConfig} config
 */
function emitMinimalHtml(config) {
  const cols = getEnabledPortalFields(config);
  const brandingEnabled = isBrandingEnabled(config);
  const branding = resolveBranding(config);
  const pageTitle = config.script?.pageTitle || 'Portal';
  const lines = [];
  lines.push('<!DOCTYPE html>');
  lines.push('<html>');
  lines.push('  <head>');
  lines.push('    <base target="_top">');
  lines.push(`    <title>${escHtml(pageTitle)}</title>`);
  lines.push('    <style>');
  if (brandingEnabled) {
    lines.push(emitBrandingCssVars(branding));
    lines.push(PORTAL_BRAND_CSS);
  }
  lines.push(`      body { font-family: system-ui, sans-serif; padding: 24px; background: var(--pb-bg, #ffffff); color: var(--pb-text, #334155); margin: 0; }`);
  lines.push('    </style>');
  lines.push('  </head>');
  lines.push('  <body>');
  if (brandingEnabled) {
    emitBrandingHeader(lines, branding, { heading: pageTitle, escHtml, wrapperClass: 'portal-brand' });
  }
  lines.push('    <? if (data.found) { ?>');
  for (const col of cols) {
    lines.push(`      <p>${escHtml(col.label)}: <?= data.${col.columnKey} ?></p>`);
  }
  lines.push('    <? } else { ?>');
  lines.push('      <p>Status: <?= data.status ?></p>');
  lines.push('    <? } ?>');
  lines.push('  </body>');
  lines.push('</html>');
  return lines.join('\n');
}

/**
 * @param {import('./config_schema.js').PortalConfig} config
 */
function emitShippingTrackerHtml(config) {
  const sections = config.html?.sections || {};
  const st = sections.shippingTracker || {};
  const search = sections.search || {};
  const urlParam = (config.sheet.urlParam || 'trackingNumber').replace(/[^\w]/g, '') || 'trackingNumber';
  const pageTitle = config.script?.pageTitle || 'Shipment Tracking';

  const brandingEnabled = isBrandingEnabled(config);
  const branding = resolveBranding(config);
  const heading = st.pageHeading || pageTitle;
  const subheading = st.pageSubheading || '';
  const introEn = st.introEn || '';
  const searchHelpEn = st.searchHelpEn || '';
  const searchHelpShort = st.searchHelpShort || '';
  const footerText = st.footerText || '';
  const orderNotFound = st.orderNotFound || sections.notFound?.message || 'Order not found.';
  const shipmentNotFound = st.shipmentNotFound || 'Tracking number not found.';

  const lines = [];
  lines.push('<!DOCTYPE html>');
  lines.push('<html>');
  lines.push('  <head>');
  lines.push('    <base target="_top">');
  lines.push(`    <title>${escHtml(pageTitle)}</title>`);
  lines.push('    <style>');
  if (brandingEnabled) {
    lines.push(emitBrandingCssVars(branding));
    lines.push(PORTAL_BRAND_CSS);
  }
  lines.push(SHIPPING_TRACKER_CSS);
  lines.push('    </style>');
  lines.push('  </head>');
  lines.push('  <body>');

  if (brandingEnabled) {
    emitBrandingHeader(lines, branding, {
      heading,
      subheading,
      escHtml,
      wrapperClass: 'header portal-brand',
      skipDescription: true
    });
  } else if (heading || subheading) {
    lines.push('    <div class="header">');
    if (heading || subheading) {
      lines.push('      <div class="header-text">');
      if (heading) {
        lines.push(`        <div class="header-title">${escHtml(heading)}</div>`);
      }
      if (subheading) {
        lines.push(`        <div class="header-subtitle">${escHtml(subheading)}</div>`);
      }
      lines.push('      </div>');
    }
    lines.push('    </div>');
  }

  lines.push('    <div class="container">');

  if (brandingEnabled) {
    emitBrandingDescription(lines, branding, escHtml);
  }

  if (introEn && !branding.description) {
    lines.push('      <div class="card-top full-width">');
    lines.push(`        <p class="muted">${escHtml(introEn)}</p>`);
    lines.push('      </div>');
  }

  if (search.enabled !== false) {
    lines.push('      <div class="card">');
    lines.push(`        <form method="get" action="<?= data.webappUrl ?>">`);
    lines.push('          <div class="row">');
    lines.push(`            <div class="label">${escHtml(search.label || 'Search confirmation no')}</div>`);
    lines.push(
      `            <input type="text" name="${urlParam}" placeholder="${escHtml(search.placeholder || '')}" required class="input">`
    );
    lines.push(`            <button type="submit" class="btn">${escHtml(search.buttonText || 'Search')}</button>`);
    lines.push('          </div>');
    lines.push('        </form>');
    lines.push('      </div>');
  }

  if (searchHelpEn || searchHelpShort) {
    lines.push('      <div class="card-white">');
    if (searchHelpEn) {
      lines.push(`        <p>${escHtml(searchHelpEn)}</p>`);
    }
    if (searchHelpShort) {
      lines.push(`        <p class="muted">${escHtml(searchHelpShort)}</p>`);
    }
    lines.push('      </div>');
  }

  lines.push('      <div class="card">');
  lines.push('        <? if (data.found) { ?>');
  lines.push('          <h1>Your order</h1>');
  lines.push('          <div class="row"><div class="label">Client</div><div class="value"><?= data.client ?></div></div>');
  lines.push(
    '          <div class="row"><div class="label">Confirmation no</div><div class="value"><?= data.searchNumber ?></div></div>'
  );
  lines.push('          <div class="row"><div class="label">PO no</div><div class="value"><?= data.poNo ?></div></div>');
  lines.push(
    '          <div class="row"><div class="label">Invoice no</div><div class="value"><?= data.invoiceNo ?></div></div>'
  );
  lines.push('        <? } else { ?>');
  lines.push(`          <div class="status not-found">${escHtml(orderNotFound)}</div>`);
  lines.push('        <? } ?>');
  lines.push('      </div>');

  lines.push('      <div class="card">');
  lines.push('        <h1>Shipment status</h1>');
  lines.push('        <? if (data.found) { ?>');
  lines.push('          <div class="row"><div class="label">Status</div><div class="value"><?= data.displayStatus ?></div></div>');
  lines.push('          <div class="status">');
  lines.push('            <? if (data.inQueue) { ?>');
  lines.push('              <div class="value"><?= data.displayStatus ?></div>');
  lines.push('            <? } else { ?>');
  lines.push('              <div class="row"><div class="label">Carrier</div><div class="value"><?= data.carrier ?></div></div>');
  lines.push(
    '              <div class="row"><div class="label">Tracking no</div><div class="value"><?= data.displayTrackingNo ?></div></div>'
  );
  lines.push('              <? if (data.displayTrackingLink) { ?>');
  lines.push('                <a href="<?= data.displayTrackingLink ?>" target="_blank" rel="noopener">');
  lines.push('                  Open carrier tracking →');
  lines.push('                </a>');
  lines.push('              <? } ?>');
  lines.push('            <? } ?>');
  lines.push('          </div>');
  lines.push('        <? } else { ?>');
  lines.push(`          <div class="status not-found">${escHtml(shipmentNotFound)}</div>`);
  lines.push('        <? } ?>');
  lines.push('      </div>');

  lines.push('      <? if (data.found) { ?>');
  lines.push('        <div class="card full-width">');
  lines.push('          <h1>Items shipped</h1>');
  lines.push(
    '          <div class="row"><div class="label">Shipped items</div><div class="value"><?= data.shippedItems ?></div></div>'
  );
  lines.push(
    '          <div class="row"><div class="label">Back order</div><div class="value"><?= data.backOrder ?></div></div>'
  );
  lines.push('        </div>');
  lines.push('      <? } ?>');

  if (footerText) {
    lines.push('      <div class="card-top full-width">');
    lines.push(`        <p class="muted">${escHtml(footerText)}</p>`);
    lines.push('      </div>');
  }

  lines.push('    </div>');
  lines.push('  </body>');
  lines.push('</html>');

  return lines.join('\n');
}

const SHIPPING_TRACKER_CSS = `
      body {
        font-family: system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
        background: var(--pb-bg, #ffffff);
        padding: 32px 24px;
        color: var(--pb-text, #475569);
        margin: 0;
      }
      .header {
        max-width: 1100px;
        margin: 0 auto 24px;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
      }
      .header-brand {
        font-size: 14px;
        font-weight: 700;
        color: var(--pb-text, #64748b);
        letter-spacing: 0.04em;
      }
      .header-text { text-align: right; line-height: 1.3; }
      .header-title {
        font-size: 16px;
        font-weight: 700;
        letter-spacing: 0.06em;
        color: var(--pb-text, #64748b);
      }
      .header-subtitle {
        font-size: 12px;
        letter-spacing: 0.06em;
        color: #94a3b8;
      }
      .container {
        max-width: 1100px;
        margin: 0 auto;
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px;
      }
      .full-width { grid-column: 1 / -1; }
      .card {
        margin: 6px;
        background: var(--pb-card, #e6e6e6);
        border-radius: 20px;
        padding: 16px 20px;
        box-shadow: 0 4px 10px rgba(0,0,0,0.06);
      }
      .card-white {
        margin: 6px;
        background: #ffffff;
        border-radius: 20px;
        padding: 14px 18px;
        font-size: 12px;
        color: #64748b;
      }
      .card-top {
        margin: 6px;
        background: #ffffff;
        border-radius: 20px;
        padding: 14px 18px;
        font-size: 13px;
      }
      h1 {
        margin: 0 0 4px;
        font-size: 20px;
        color: #64748b;
      }
      .row { margin-bottom: 12px; }
      .label {
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #64748b;
      }
      .value { font-size: 16px; font-weight: 500; }
      .status {
        padding: 10px 14px;
        background: #eef2ff;
        border-radius: 8px;
        margin-top: 8px;
      }
      .not-found { background: #fff4f4; color: #b91c1c; }
      .muted { color: #94a3b8; line-height: 1.5; margin: 0 0 8px; }
      a { color: var(--pb-primary, #2563eb); text-decoration: none; font-weight: 500; }
      .input {
        width: min(100%, 260px);
        padding: 8px 10px;
        font-size: 16px;
        border-radius: 8px;
        border: 1px solid #cbd5e1;
        margin-top: 6px;
        margin-right: 8px;
      }
      .btn {
        margin-top: 8px;
        padding: 8px 18px;
        font-size: 15px;
        font-weight: 500;
        border-radius: 8px;
        border: none;
        background: var(--pb-primary, #2563eb);
        color: white;
        cursor: pointer;
      }
      @media (max-width: 720px) {
        .container { grid-template-columns: 1fr; }
        .header { flex-direction: column; }
        .header-text { text-align: left; }
      }
`;

const EMBED_CSS = `
      body {
        font-family: system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
        background: var(--pb-bg, #ffffff);
        padding: 32px 24px;
        color: var(--pb-text, #334155);
        margin: 0;
      }
      .wrap { max-width: 1100px; margin: 0 auto; }
      .intro { margin-bottom: 20px; }
      .intro-title { font-size: 22px; color: var(--pb-text, #475569); margin: 0 0 8px; }
      .intro-sub { font-size: 14px; color: #94a3b8; margin: 0 0 8px; }
      .muted { color: #64748b; font-size: 15px; line-height: 1.5; }
      .card {
        border-radius: 20px;
        padding: 20px 24px;
        margin-bottom: 16px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.06);
        background: var(--pb-card, #e6e6e6);
      }
      .card.full-width { grid-column: 1 / -1; }
      .card-title { margin: 0 0 12px; font-size: 18px; color: var(--pb-text, #475569); }
      .row { margin-bottom: 12px; }
      .label {
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #64748b;
      }
      .value { font-size: 16px; font-weight: 500; }
      .status { padding: 12px 14px; border-radius: 8px; margin-top: 8px; }
      .not-found { background: #fff4f4; color: #b91c1c; }
      .input {
        width: 100%;
        max-width: 280px;
        padding: 10px 12px;
        font-size: 16px;
        border-radius: 8px;
        border: 1px solid #cbd5e1;
        margin-top: 6px;
      }
      .btn {
        margin-top: 12px;
        padding: 10px 16px;
        font-size: 15px;
        font-weight: 500;
        border-radius: 8px;
        border: none;
        background: var(--pb-primary, #2563eb);
        color: white;
        cursor: pointer;
      }
      a { color: var(--pb-primary, #2563eb); }
      .renew-form { margin-top: 16px; }
`;
